import Phaser from 'phaser';
import { CONSTANTS } from '../game.js';
import { SoundManager } from '../managers/SoundManager.js';

/**
 * ゴム紐の物理特性と視覚効果を管理するクラス
 */
export class RubberBand {
    // パーフェクト状態のアニメーション用の定数
    static PERFECT_ANIMATION = {
        COLORS: [0xff00ff, 0xff00aa, 0xff0055, 0xff0000, 0xff5500, 0xffaa00, 0xffff00, 0xaaff00, 0x55ff00, 0x00ff00, 0x00ff55, 0x00ffaa, 0x00ffff, 0x00aaff, 0x0055ff, 0x0000ff, 0x5500ff, 0xaa00ff],
        COLOR_CHANGE_SPEED: 100, // ミリ秒ごとに色を変更
        PARTICLE_EMIT_INTERVAL: 100 // ミリ秒ごとにパーティクルを放出
    };
    /**
     * @param {Phaser.Scene} scene - シーン
     * @param {Weight} weight - 錘オブジェクト
     * @param {Object} anchorPoint - アンカーポイント
     * @param {Object} callbacks - コールバック関数のオブジェクト
     * @param {Function} [callbacks.onNaturalLengthChanged] - 自然長変更時のコールバック
     * @param {Function} [callbacks.onBroken] - ゴム紐切断時のコールバック
     * @param {Function} [callbacks.onEnterCritical] - クリティカル状態に入った時のコールバック
     * @param {Function} [callbacks.onRecoverFromCritical] - クリティカル状態から回復した時のコールバック
     */
    constructor(scene, weight, anchorPoint, callbacks = {}) {
        this.scene = scene;
        this.weight = weight;
        this.anchorPoint = anchorPoint;
        this.callbacks = callbacks;
        this.graphics = scene.add.graphics();
        this.graphics.setDepth(14); // コンボ表示より前面、錘より背面に表示
        this.constraint = null;
        this.broken = false;
        this.stretchRatio = 0;
        this.currentLength = 0;
        
        // 自然長と最大長さをインスタンス変数として保持
        this.naturalLength = CONSTANTS.RUBBER_BAND.NATURAL_LENGTH;
        
        // クリティカル状態の管理
        this.criticalState = false;
        
        // パーフェクト状態の管理
        this.isPerfect = false;
        this.perfectColorIndex = 0;
        this.lastColorChangeTime = 0;
        this.lastParticleEmitTime = 0;
        
        // パーフェクト状態用のパーティクルエミッター
        this.sparkleEmitter = null;
        
        this.createConstraint();
        this.setupPerfectEffects();
    }
    
    /**
     * パーフェクト状態のエフェクトを設定
     */
    setupPerfectEffects() {
        // キラキラパーティクル用のエミッターを作成
        if (this.scene.textures && this.scene.textures.exists && this.scene.textures.exists('sparkle')) {
            this.sparkleEmitter = this.scene.add.particles(0, 0, 'sparkle', {
                lifespan: 800,
                speed: { min: 20, max: 50 },
                scale: { start: 0.6, end: 0 },
                alpha: { start: 0.8, end: 0 },
                blendMode: 'ADD',
                emitting: false
            });
            this.sparkleEmitter.setDepth(13); // ゴム紐の背後に表示
        } else {
            console.warn('RubberBand: sparkleテクスチャが見つかりません');
        }
    }
    
    createConstraint() {
        // Matter.jsの制約を作成

        try {
            const weightBody = this.weight.getBody();
            if (!weightBody) {
                console.error('Weight body is not available');
                return;
            }

            // Phaser 3.88.2 での制約の作成方法
            const config = {
                pointA: { x: this.anchorPoint.x, y: this.anchorPoint.y },
                bodyB: weightBody,
                length: this.naturalLength, // 自然長を設定
                stiffness: CONSTANTS.RUBBER_BAND.STIFFNESS,
                damping: CONSTANTS.RUBBER_BAND.DAMPING
            };
            console.log('Creating constraint with config:', config);
            this.constraint = Phaser.Physics.Matter.Matter.Constraint.create(config);
            console.log('Created constraint:', this.constraint);
            
            // 制約をワールドに追加
            this.scene.matter.world.add(this.constraint);
            
            console.log('Constraint created successfully with natural length:', this.naturalLength);
        } catch (error) {
            console.error('Error creating constraint:', error);
        }
    }

    /**
     * 現在の長さを計算する
     * @private
     * @returns {number} 計算された現在の長さ
     */
    calculateCurrentLength() {
        const weightBody = this.weight?.getBody();
        const weightPos = weightBody?.position;
        
        
        const dx = this.anchorPoint.x - weightPos.x;
        const dy = this.anchorPoint.y - weightPos.y;
        return Math.sqrt(dx * dx + dy * dy);
    }

    /**
     * 伸び率を計算する
     * @private
     * @param {number} length - 現在の長さ
     * @returns {number} 計算された伸び率（0-1の範囲）
     */
    calculateStretchRatio(length) {
        if (length <= this.naturalLength) {
            return 0;
        }
        
        const maxLength = this.getMaxLength();
        // 最大長さと自然長が同じか、または最大長さが自然長より短い場合
        if (maxLength <= this.naturalLength) {
            return 0;
        }
        
        const ratio = (length - this.naturalLength) / (maxLength - this.naturalLength);
        return ratio;
    }
    
    update() {
        if (this.broken) return false;
        
        // 現在の状態を計算
        this.currentLength = this.calculateCurrentLength();
        this.stretchRatio = this.calculateStretchRatio(this.currentLength);
        
        // ゴム紐の描画
        this.draw();
        
        if (this.currentLength > this.getMaxLength()) {
            // クリティカル状態
            if (!this.criticalState) {
                this.enterCriticalState();
            }
        } else if (this.criticalState) {
            this.recoverFromCritical();
        }
        this.constraint.stiffness = this.getStiffness();
        
        // クリティカル状態でもゴム紐は切れなくなった
        return false; // 切れていないことを通知
    }

    draw() {
        // 既存の描画を消去
        this.graphics.clear();
        
        const weightBody = this.weight?.getBody();
        const weightPos = weightBody?.position;
        
        // 伸び率に応じた色と線の太さを設定
        let color;
        let lineWidth;
        let isPerfectNow = false;
        
        if (this.currentLength > this.naturalLength) {
            // 伸び率に応じた色（FAILは除外）
            if (this.stretchRatio < CONSTANTS.STRETCH_EVALUATION.PASS.max) {
                color = parseInt(CONSTANTS.EVALUATION.SETTINGS.PASS.color.replace('#', '0x'), 16);
            } else if (this.stretchRatio < CONSTANTS.STRETCH_EVALUATION.GOOD.max) {
                color = parseInt(CONSTANTS.EVALUATION.SETTINGS.GOOD.color.replace('#', '0x'), 16);
            } else if (this.stretchRatio < CONSTANTS.STRETCH_EVALUATION.GREAT.max) {
                color = parseInt(CONSTANTS.EVALUATION.SETTINGS.GREAT.color.replace('#', '0x'), 16);
            } else {
                // PERFECT状態
                isPerfectNow = true;
                
                // 時間経過で色が変化するアニメーション
                const currentTime = this.scene.time.now;
                if (currentTime - this.lastColorChangeTime > RubberBand.PERFECT_ANIMATION.COLOR_CHANGE_SPEED) {
                    this.perfectColorIndex = (this.perfectColorIndex + 1) % RubberBand.PERFECT_ANIMATION.COLORS.length;
                    this.lastColorChangeTime = currentTime;
                }
                
                color = RubberBand.PERFECT_ANIMATION.COLORS[this.perfectColorIndex];
            }
            
            // ゴム紐の太さも伸縮度に応じて変化
            lineWidth = Math.max(1, 5 - this.stretchRatio * 3);
        } else {
            // 自然長以下の場合は通常表示
            color = parseInt(CONSTANTS.EVALUATION.SETTINGS.GOOD.color.replace('#', '0x'), 16); // 緑
            lineWidth = 5;
        }
        
        // パーフェクト状態の変化を検出
        if (isPerfectNow !== this.isPerfect) {
            this.isPerfect = isPerfectNow;
            if (this.isPerfect) {
                // パーフェクト状態に入った時の処理
                // console.log('パーフェクト状態に入りました');
            } else {
                // パーフェクト状態から抜けた時の処理
                // console.log('パーフェクト状態から抜けました');
            }
        }
        
        if (isPerfectNow) {
            // パーフェクト状態の特殊描画（二重線）
            
            // 外側の線（グロー効果）
            this.graphics.lineStyle(lineWidth + 4, color, 0.3);
            this.graphics.beginPath();
            this.graphics.moveTo(weightPos.x, weightPos.y);
            this.graphics.lineTo(this.anchorPoint.x, this.anchorPoint.y);
            this.graphics.closePath();
            this.graphics.strokePath();
            
            // 中間の線
            this.graphics.lineStyle(lineWidth + 2, color, 0.6);
            this.graphics.beginPath();
            this.graphics.moveTo(weightPos.x, weightPos.y);
            this.graphics.lineTo(this.anchorPoint.x, this.anchorPoint.y);
            this.graphics.closePath();
            this.graphics.strokePath();
            
            // 内側の線（メイン）
            this.graphics.lineStyle(lineWidth, color, 1);
            this.graphics.beginPath();
            this.graphics.moveTo(weightPos.x, weightPos.y);
            this.graphics.lineTo(this.anchorPoint.x, this.anchorPoint.y);
            this.graphics.closePath();
            this.graphics.strokePath();
            
            // キラキラパーティクルの放出
            if (this.sparkleEmitter && this.scene.time.now - this.lastParticleEmitTime > RubberBand.PERFECT_ANIMATION.PARTICLE_EMIT_INTERVAL) {
                this.lastParticleEmitTime = this.scene.time.now;
                
                // ゴム紐に沿って数カ所からパーティクルを放出
                const segments = 5; // 分割数
                for (let i = 1; i < segments; i++) {
                    const t = i / segments;
                    const x = Phaser.Math.Linear(weightPos.x, this.anchorPoint.x, t);
                    const y = Phaser.Math.Linear(weightPos.y, this.anchorPoint.y, t);
                    
                    // パーティクルの位置を設定
                    this.sparkleEmitter.setPosition(x, y);
                    
                    // パーティクルを放出（色はエミッター作成時に設定）
                    this.sparkleEmitter.explode(2, x, y); // 少量のパーティクルを放出
                }
            }
        } else {
            // 通常の描画（単一の線）
            this.graphics.lineStyle(lineWidth, color);
            this.graphics.beginPath();
            this.graphics.moveTo(weightPos.x, weightPos.y);
            this.graphics.lineTo(this.anchorPoint.x, this.anchorPoint.y);
            this.graphics.closePath();
            this.graphics.strokePath();
        }
    }
    
    
    /**
     * クリティカル状態に入る
     */
    enterCriticalState() {
        this.criticalState = true;
        
        // 自然長を短くする処理を削除
        
        // クリティカル状態に入ったことを通知するコールバック
        if (this.callbacks.onEnterCritical) {
            this.callbacks.onEnterCritical();
        }
        
        // 画面全体のフラッシュエフェクト
        const flash = this.scene.add.rectangle(
            0, 0,
            this.scene.game.config.width,
            this.scene.game.config.height,
            0xff0000
        );
        flash.setOrigin(0, 0);
        flash.setDepth(1000); // 最前面に表示
        flash.setAlpha(0);

        // フラッシュアニメーション
        this.scene.tweens.add({
            targets: flash,
            alpha: { from: 0.6, to: 0 },
            duration: 100,
            yoyo: true,
            repeat: 1,
            onComplete: () => {
                flash.destroy();
            }
        });

        // SoundManagerを使用して警告音を再生
        const soundManager = new SoundManager(this.scene);
        soundManager.playCriticalSound();
    }
    
    recoverFromCritical() {
        this.criticalState = false;

        // クリティカル状態から回復したことを通知するコールバック
        if (this.callbacks.onRecoverFromCritical) {
            this.callbacks.onRecoverFromCritical();
        }

        try {
            const weightBody = this.weight?.getBody();
            const weightPos = weightBody?.position;
            
            // 回復エフェクト（緑の輪）
            const recovery = this.scene.add.graphics();
            recovery.setDepth(15);
            recovery.lineStyle(3, 0x00ff00);
            
            // テスト環境ではstrokeCircleが存在しない可能性があるため、try-catchで囲む
            try {
                recovery.strokeCircle(weightPos.x, weightPos.y, 30);
            } catch (error) {
                console.error('Error drawing recovery circle:', error);
                // テスト環境ではエフェクトを描画しない
            }
            
            // エフェクトのフェードアウト
            this.scene.tweens.add({
                targets: recovery,
                alpha: 0,
                duration: 200,
                onComplete: () => {
                    recovery.destroy();
                }
            });
        } catch (error) {
            console.error('Error in recoverFromCritical:', error);
            // テスト環境ではエフェクトを描画しない
        }
    }

    break() {
        if (this.broken) return;
        
        this.broken = true;
        
        if (this.constraint) {
            try {
                this.scene.matter.world.removeConstraint(this.constraint);
                this.constraint = null;
                console.log('Constraint removed');
                
                if (this.weight && this.weight.handleRubberBreak) {
                    this.weight.handleRubberBreak();
                }
                
            } catch (error) {
                console.error('Error removing constraint:', error);
            }
        }
        
        this.createBreakEffect();
        this.graphics.clear();
        
        // コールバック関数を呼び出す
        if (this.callbacks.onBroken) {
            this.callbacks.onBroken();
        }
    }
    
    createBreakEffect() {
        const weightBody = this.weight?.getBody();
        const weightPos = weightBody?.position;
        
        const breakX = (this.anchorPoint.x + weightPos.x) / 2;
        const breakY = (this.anchorPoint.y + weightPos.y) / 2;
        
        const particles = this.scene.add.particles(breakX, breakY, 'weight', {
            speed: { min: 50, max: 150 },
            angle: { min: 0, max: 360 },
            scale: { start: 0.2, end: 0 },
            lifespan: 800,
            quantity: 20,
            blendMode: 'ADD',
            emitting: false
        });
        
        particles.setDepth(16);
        particles.explode(20, 0, 0);
        
        this.scene.time.delayedCall(900, () => {
            particles.destroy();
        });
    }
    
    setAnchorPosition(x, y) {
        this.anchorPoint.x = x;
        this.anchorPoint.y = y;
        
        if (this.constraint && !this.broken) {
            this.constraint.pointA = { x, y };
        }
    }
    
    updateWeightBody(oldBody, newBody) {
        if (this.broken) return;
        
        if (this.constraint) {
            this.scene.matter.world.removeConstraint(this.constraint);
        }
        
        this.weight = newBody;
        this.createConstraint();
    }
    
    /**
     * 現在の状態に応じた張力値を取得
     * @returns {number} 張力値
     */
    getStiffness() {
        if (this.currentLength > this.getMaxLength()) {
            // クリティカル状態でも通常の張力を維持
            return CONSTANTS.RUBBER_BAND.STIFFNESS;
        } else if (this.currentLength > this.naturalLength) {
            // 通常の伸張状態
            return CONSTANTS.RUBBER_BAND.STIFFNESS;
        } else {
            // 自然長以下の状態
            return 0;
        }
    }
    
    /**
     * 現在の最大長さを取得
     * @returns {number} 最大長さ
     */
    getMaxLength() {
        return this.naturalLength * CONSTANTS.RUBBER_BAND.MAX_STRETCH_FACTOR;
    }
    
    /**
     * 現在の自然長を取得
     * @returns {number} 自然長
     */
    getNaturalLength() {
        return this.naturalLength;
    }
    
    /**
     * 現在の長さを取得
     * @returns {number} 現在の長さ
     */
    getCurrentLength() {
        return this.currentLength;
    }
    
    /**
     * 現在の伸び率を取得（0-1の範囲）
     * @returns {number} 伸び率
     */
    getStretchRatio() {
        return this.stretchRatio;
    }
    
    getWeight() {
        return this.weight;
    }
    
    isBroken() {
        return this.broken;
    }
    
    destroy() {
        if (!this.broken && this.constraint) {
            try {
                this.scene.matter.world.removeConstraint(this.constraint);
            } catch (error) {
                console.error('Error removing constraint:', error);
            }
        }
        
        // パーティクルエミッターの破棄
        if (this.sparkleEmitter) {
            this.sparkleEmitter.destroy();
            this.sparkleEmitter = null;
        }
        
        this.graphics.clear();
        this.graphics.destroy();
    }
}
